/**
 * FEEDBACK PAGE
 * 
 * Features:
 * - Bug report form
 * - Feature suggestion form
 * - General feedback
 * - Status of submitted feedback (future)
 */

import { useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, Send, CheckCircle, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { PageHeader } from '../components/common';

type FeedbackType = 'bug' | 'feature' | 'general';

export default function FeedbackPage() {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const feedbackTypes = [
    { id: 'bug' as FeedbackType, icon: Bug, label: 'Report a Bug', color: 'text-red-400 bg-slate-800 border-red-500' },
    { id: 'feature' as FeedbackType, icon: Lightbulb, label: 'Suggest a Feature', color: 'text-yellow-400 bg-slate-800 border-yellow-500' },
    { id: 'general' as FeedbackType, icon: MessageSquarePlus, label: 'General Feedback', color: 'text-glass bg-slate-700 border-slate-600' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Get the label for the selected feedback type (e.g., "Report a Bug")
      const feedbackLabel = feedbackTypes.find(t => t.id === feedbackType)?.label || feedbackType;

      // Send via EmailJS using the same service/template as the Contact page
      await emailjs.send(
        'service_mspkyt5',
        'template_vjfdxqo',
        {
          from_name: email || 'Anonymous User',
          from_email: email || 'no-reply@polishallthethings.com',
          category: `📋 Feedback: ${feedbackLabel}`,
          message: `**${title}**\n\n${description}`,
        },
        'TnFGInG6cAnf9y9vV'
      );

      setIsSubmitted(true);

      // Reset form after delay
      setTimeout(() => {
        setTitle('');
        setDescription('');
        setEmail('');
        setIsSubmitted(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending feedback:', error);
      setSubmitError('Failed to send feedback. Please try again or email us at hello@polishallthethings.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="icon-xl text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-glass mb-4 font-heading">
              Thank You!
            </h2>
            <p className="text-glass-secondary font-body">
              Your feedback has been received. We appreciate you taking the time to help us improve!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* HEADER */}
        <PageHeader
          icon={MessageSquarePlus}
          title="Feedback & Bugs"
          subtitle="Help us make Polish ALL the Things even better!"
          className="mb-6"
        />

        {/* Error Message */}
        {submitError && (
          <div className="glass-card-sm p-4 mb-6 flex items-start gap-3 border-l-4 border-l-red-500">
            <AlertCircle className="icon-base text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-glass font-body">
                Oops! Something went wrong
              </h3>
              <p className="text-glass-secondary text-sm mt-1">
                {submitError}
              </p>
            </div>
          </div>
        )}

        {/* Feedback Type Selector */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {feedbackTypes.map(type => {
            const Icon = type.icon;
            const isSelected = feedbackType === type.id;
            return (
              <button
                key={type.id}
                onClick={() => setFeedbackType(type.id)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  isSelected
                    ? type.color + ' border-current'
                    : 'bg-slate-800/60 border-slate-600 hover:bg-slate-700 text-glass-secondary'
                }`}
              >
                <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? '' : 'text-glass-secondary'}`} />
                <span className={`text-sm font-medium font-body ${isSelected ? '' : 'text-glass-secondary'}`}>
                  {type.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} className="glass-card p-8">
          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
              {feedbackType === 'bug' ? 'What went wrong?' : feedbackType === 'feature' ? 'What would you like to see?' : 'Subject'}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={
                feedbackType === 'bug'
                  ? 'e.g., Photos not uploading on mobile'
                  : feedbackType === 'feature'
                    ? 'e.g., Add barcode scanning for polish'
                    : 'e.g., Love the new design!'
              }
              className="w-full px-4 py-3 rounded-xl border border-slate-600 focus:ring-2 focus:ring-slate-500 focus:border-slate-600 bg-slate-700/40 text-glass placeholder:text-glass-muted font-body"
            />
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
              {feedbackType === 'bug' ? 'Steps to reproduce' : 'Tell us more'}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows={5}
              placeholder={
                feedbackType === 'bug'
                  ? '1. Go to...\n2. Click on...\n3. See error...'
                  : feedbackType === 'feature'
                    ? 'Describe how this feature would work and why it would be helpful...'
                    : 'Share your thoughts...'
              }
              className="w-full px-4 py-3 rounded-xl border border-slate-600 focus:ring-2 focus:ring-slate-500 focus:border-slate-600 resize-none bg-slate-700/40 text-glass placeholder:text-glass-muted font-body"
            />
          </div>

          {/* Email (optional) */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-glass-secondary mb-2 font-body">
              Email (optional)
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-xl border border-slate-600 focus:ring-2 focus:ring-slate-500 focus:border-slate-600 bg-slate-700/40 text-glass placeholder:text-glass-muted font-body"
            />
            <p className="text-xs text-glass-secondary mt-1">
              If you'd like us to follow up with you about this feedback
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !title || !description}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-fuchsia-600/90 to-purple-600/90 backdrop-blur-sm text-white rounded-xl font-bold text-lg hover:from-fuchsia-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl border border-fuchsia-500/30 font-body"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-slate-600 border-t-white rounded-full animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="icon-md" />
                Send Feedback
              </>
            )}
          </button>
        </form>

        {/* Note */}
        <p className="text-center text-sm text-on-bg font-semibold mt-6 font-body">
          By submitting feedback, you help us prioritize improvements. Thank you! 💅
        </p>
      </div>
    </div>
  );
}
